import { IntlShape } from 'react-intl';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import {
  AttachmentsScope,
  DEFAULT_ISSUE_ATTACHMENTS_CONFIG,
  DEFAULT_MESSAGE_PRESENTATION,
  DEFAULT_MESSAGE_SECTION_CONFIG,
  DEFAULT_SECTION_CONFIG,
  ImageDisplayStyle,
  IssueAttachmentsConfig,
  MessagePresentation,
  MessageSectionConfig,
  ReportFieldsContent,
  ReportType,
  SectionConfig,
} from 'shared/domain/report/types/model/common';
import { ImageContent } from 'shared/domain/report/types/model/pdfReportConfigLegacy';
import { Field } from 'shared/domain/visibleField/availableFields';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { LocaleCountryAndLanguageCode } from 'shared/types/locale';
import { LocalStorage } from './LocalStorage';
import { LocalStorageKeyFactory } from './keys';

export type SavedReportConfig = {
  messagePresentation: MessagePresentation;
  issueAttachments: IssueAttachmentsConfig;
  linkedDocumentation: SectionConfig;
  events: MessageSectionConfig;
  incomingEmailMessages: MessageSectionConfig;
  workflowDecisions: MessageSectionConfig;
  showPhoneNumbers: boolean;
  showUserEmails: boolean;
  showFileNames: boolean;
  showFileDescriptions: boolean;
  showActiveLinks: boolean;
  /** Migrated to `showActiveLinks` on read. */
  fileNamesLinkMode?: string;
  /** Read from older saved configs only — not persisted on save. */
  showImagesNamesAndDescriptions?: boolean;
};

const DEFAULT_SAVED_REPORT_CONFIG: SavedReportConfig = {
  messagePresentation: DEFAULT_MESSAGE_PRESENTATION,
  issueAttachments: DEFAULT_ISSUE_ATTACHMENTS_CONFIG,
  linkedDocumentation: DEFAULT_SECTION_CONFIG,
  events: DEFAULT_MESSAGE_SECTION_CONFIG,
  incomingEmailMessages: DEFAULT_MESSAGE_SECTION_CONFIG,
  workflowDecisions: DEFAULT_MESSAGE_SECTION_CONFIG,
  showPhoneNumbers: false,
  showUserEmails: false,
  showFileNames: false,
  showFileDescriptions: false,
  showActiveLinks: false,
};

function normalizeSavedTextMetadata(
  saved: Partial<SavedReportConfig>
): Pick<SavedReportConfig, 'showFileNames' | 'showFileDescriptions' | 'showActiveLinks'> {
  if (saved.showFileNames !== undefined) {
    return {
      showFileNames: saved.showFileNames,
      showFileDescriptions: saved.showFileDescriptions ?? false,
      showActiveLinks:
        saved.showActiveLinks ?? migrateFileNamesLinkModeToActiveLinks(saved),
    };
  }

  const legacyEnabled = saved.showImagesNamesAndDescriptions ?? false;
  return {
    showFileNames: legacyEnabled,
    showFileDescriptions: legacyEnabled,
    showActiveLinks: legacyEnabled,
  };
}

function migrateFileNamesLinkModeToActiveLinks(
  saved: Pick<SavedReportConfig, 'fileNamesLinkMode'>
): boolean {
  if (saved.fileNamesLinkMode === 'without_link') {
    return false;
  }
  if (saved.fileNamesLinkMode === 'with_link') {
    return true;
  }
  return false;
}

export function migrateLegacyReportLocalStorage(reportType: ReportType): void {
  const configKey = LocalStorageKeyFactory.createReportConfigKey(reportType);
  if (LocalStorage.get<SavedReportConfig | null>(configKey, null)) {
    return;
  }

  const imageContent = LocalStorage.get<ImageContent | null>(
    LocalStorageKeyFactory.createReportImageContentKey(reportType),
    null
  );
  const showMessage = LocalStorage.get<boolean | null>(
    LocalStorageKeyFactory.createReportShowMessageKey(reportType),
    null
  );
  const showPhoneNumbers = LocalStorage.get<boolean | null>(
    LocalStorageKeyFactory.createReportShowPhoneNumbersKey(reportType),
    null
  );
  const showImagesNamesAndDescriptions = LocalStorage.get<boolean | null>(
    LocalStorageKeyFactory.createReportShowImagesNamesAndDescriptionsKey(
      reportType
    ),
    null
  );

  const hasAnyLegacyValue =
    imageContent !== null ||
    showMessage !== null ||
    showPhoneNumbers !== null ||
    showImagesNamesAndDescriptions !== null;
  if (!hasAnyLegacyValue) {
    return;
  }

  const config =
    imageContent !== null
      ? mapLegacyToSavedReportConfig(imageContent, showMessage ?? false)
      : {
          ...DEFAULT_SAVED_REPORT_CONFIG,
          messagePresentation: {
            inEmailNotification: showMessage ?? false,
            inPdfReport: showMessage ?? false,
          },
        };
  config.showPhoneNumbers = showPhoneNumbers ?? false;
  const textMetadata = normalizeSavedTextMetadata({
    showImagesNamesAndDescriptions: showImagesNamesAndDescriptions ?? false,
  });
  config.showFileNames = textMetadata.showFileNames;
  config.showFileDescriptions = textMetadata.showFileDescriptions;
  config.showActiveLinks = textMetadata.showActiveLinks;

  LocalStorage.set(configKey, config);

  LocalStorage.delete(
    LocalStorageKeyFactory.createReportImageContentKey(reportType)
  );
  LocalStorage.delete(
    LocalStorageKeyFactory.createReportShowMessageKey(reportType)
  );
  LocalStorage.delete(
    LocalStorageKeyFactory.createReportShowPhoneNumbersKey(reportType)
  );
  LocalStorage.delete(
    LocalStorageKeyFactory.createReportShowImagesNamesAndDescriptionsKey(
      reportType
    )
  );
}

function mapLegacyToSavedReportConfig(
  imageContent: ImageContent,
  showMessage: boolean
): SavedReportConfig {
  const issueAttachments = legacyIssueAttachments(imageContent);
  const messageAttachments = legacyMessageAttachments(imageContent);
  return {
    messagePresentation: {
      inEmailNotification: showMessage,
      inPdfReport: showMessage,
    },
    issueAttachments,
    linkedDocumentation: {
      enabled: issueAttachments.enabled,
      displayStyle: issueAttachments.displayStyle,
    },
    events: {
      enabled: false,
      attachments: messageAttachments,
    },
    incomingEmailMessages: {
      enabled: false,
      attachments: messageAttachments,
    },
    workflowDecisions: {
      enabled: false,
      attachments: messageAttachments,
    },
    showPhoneNumbers: false,
    showUserEmails: false,
    showFileNames: false,
    showFileDescriptions: false,
    showActiveLinks: false,
  };
}

function legacyIssueAttachments(
  imageContent: ImageContent
): IssueAttachmentsConfig {
  return {
    enabled: imageContent !== ImageContent.none,
    scope:
      imageContent === ImageContent.main
        ? AttachmentsScope.main
        : AttachmentsScope.all,
    displayStyle: legacyDisplayStyleFor(imageContent),
  };
}

function legacyMessageAttachments(imageContent: ImageContent): SectionConfig {
  if (
    imageContent === ImageContent.none ||
    imageContent === ImageContent.main
  ) {
    return { enabled: true, displayStyle: ImageDisplayStyle.list };
  }
  return { enabled: true, displayStyle: legacyDisplayStyleFor(imageContent) };
}

function legacyDisplayStyleFor(
  imageContent: ImageContent
): ImageDisplayStyle {
  switch (imageContent) {
    case ImageContent.all:
      return ImageDisplayStyle.fullPage;
    case ImageContent.allAsMiniatures:
    case ImageContent.main:
    case ImageContent.none:
      return ImageDisplayStyle.miniatures;
  }
}

export function getSavedReportConfig(
  reportType: ReportType
): SavedReportConfig {
  migrateLegacyReportLocalStorage(reportType);
  const key = LocalStorageKeyFactory.createReportConfigKey(reportType);
  const saved = LocalStorage.get<SavedReportConfig | null>(key, null);
  if (!saved) {
    return { ...DEFAULT_SAVED_REPORT_CONFIG };
  }
  const merged = { ...DEFAULT_SAVED_REPORT_CONFIG, ...saved };
  const { showFileNames, showFileDescriptions, showActiveLinks } =
    normalizeSavedTextMetadata(merged);
  return {
    ...merged,
    showFileNames,
    showFileDescriptions,
    showActiveLinks,
  };
}

export function saveReportConfig(
  reportType: ReportType,
  partial: Partial<SavedReportConfig>
): void {
  const key = LocalStorageKeyFactory.createReportConfigKey(reportType);
  const current = getSavedReportConfig(reportType);
  LocalStorage.set(key, { ...current, ...partial });
}

export function getSavedReportFieldsContent(
  reportType: ReportType
): ReportFieldsContent {
  const key = LocalStorageKeyFactory.createReportFieldsContentKey(reportType);
  return LocalStorage.get<ReportFieldsContent>(key, ReportFieldsContent.filled);
}

export function saveFieldsContent(
  reportType: ReportType,
  value: ReportFieldsContent
): void {
  const key = LocalStorageKeyFactory.createReportFieldsContentKey(reportType);
  LocalStorage.set(key, value);
}

export function getSavedSelectedFields(
  reportType: ReportType,
  intl: IntlShape,
  projectId: string,
  fieldsMap: HashMap<Field>,
  processId?: string
): LabelledEntity[] {
  const key = LocalStorageKeyFactory.createReportSelectedFieldsKey(
    reportType,
    projectId,
    processId
  );

  const value = LocalStorage.get<string>(key, '');
  if (!value) {
    return [];
  }

  return value
    .split(',')
    .map((fieldName) => fieldName.trim())
    .filter((fieldName) => fieldName.length > 0)
    .flatMap((fieldName) => {
      const field = fieldsMap[fieldName];
      if (!field) {
        return [];
      }
      return [
        {
          _id: fieldName,
          label: formatTranslation(
            intl,
            unsafeTranslationKey(field.labelId)
          ),
        },
      ];
    });
}

export function saveSelectedFields(
  reportType: ReportType,
  projectId: string,
  value: string,
  processId?: string
): void {
  const key = LocalStorageKeyFactory.createReportSelectedFieldsKey(
    reportType,
    projectId,
    processId
  );
  LocalStorage.set(key, value);
}

export function getReportAdditionalContentExpanded(
  reportType: ReportType,
  accordionId?: string,
  defaultExpanded = true
): boolean {
  const key = LocalStorageKeyFactory.createReportAdditionalContentExpandedKey(
    reportType,
    accordionId
  );
  return LocalStorage.get<boolean>(key, defaultExpanded);
}

export function saveReportAdditionalContentExpanded(
  reportType: ReportType,
  expanded: boolean,
  accordionId?: string
): void {
  const key = LocalStorageKeyFactory.createReportAdditionalContentExpandedKey(
    reportType,
    accordionId
  );
  LocalStorage.set(key, expanded);
}

export function getLastReportLocale(): LocaleCountryAndLanguageCode | null {
  return LocalStorage.get<LocaleCountryAndLanguageCode | null>(
    LocalStorageKeyFactory.LAST_REPORT_LOCALE,
    null
  );
}

export function saveLastReportLocale(
  localeTag: LocaleCountryAndLanguageCode
): void {
  LocalStorage.set(LocalStorageKeyFactory.LAST_REPORT_LOCALE, localeTag);
}
